import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { Card } from '@codes/ui-kit/components/ui/card';
import { Image } from '@codes/ui-kit/components/ui/image';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { NameEditForm } from '@web/app/features/my-page/components';
import { Nav, useDialogStore, useSelectedStore } from '@web/app/shared';
import { Check, ChevronRight } from 'lucide-react';

import { useOwnerStore } from '@codes/owner';
import { Badge, Button, Input, Label, Tabs, TabsContent, TabsList, TabsTrigger } from '@codes/ui-kit';
import { UserView, useWebCoreStore } from '@codes/web-core';

import SadIcon from '/assets/icons/sad.svg';
import SadDarkIcon from '/assets/icons/sad_dark.svg';

import { BillingsPage } from '../sub-pages/billings';
import { SubscriptionsPage } from '../sub-pages/subscriptions';

type TabValue = 'info' | 'payment';

export const MyPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState<TabValue>(() => (searchParams.get('tab') as TabValue) || 'info');
    const [isExpanded, setIsExpanded] = useState(true);
    const [isPreventDeletionDialogOpen, setIsPreventDeletionDialogOpen] = useState(false);
    const ownerInfo = useOwnerStore(state => state.owner);
    const userName = useWebCoreStore(state => state.userName);
    const email = useWebCoreStore(state => state.email);
    const updateUserName = useWebCoreStore(state => state.updateUserName);

    const { workspace } = useSelectedStore();
    const isProWorkspace = workspace && workspace.stereo === 'pro';

    const { openPriceDialog } = useDialogStore(state => state);

    useEffect(() => {
        const currentTab = searchParams.get('tab') as TabValue;
        setActiveTab(currentTab || 'info');
    }, [searchParams]);

    const handleTabChange = (value: TabValue) => {
        const params = new URLSearchParams(searchParams);
        params.set('tab', value);
        navigate(`?${params.toString()}`);
    };

    const handleSuccessUpdateName = (user: UserView) => {
        updateUserName(user);
        toast({
            description: t('myPage.nameChangeComplete'),
        });
    };

    const handleClickRegister = () => {
        toast({
            description: t('myPage.preparation'),
        });
    };

    return (
        <div className="flex-1 flex flex-col w-screen ml-[-82px] max-md:ml-0">
            <Nav title={t('myPage.myPage')} />
            {!isProWorkspace && (
                <div className="bg-[#E5E6E7] dark:bg-[#373737] h-[52px] flex items-center justify-center gap-2 text-sm font-medium">
                    <Check className="w-4 h-4" />
                    {t('workspace.list.tryMore')}
                    <Badge
                        onClick={openPriceDialog}
                        variant="B"
                        className="bg-secondary ml-[10px] cursor-pointer px-3 text-sm font-normal capitalize"
                    >
                        {t('workspace.list.upgrade')}
                    </Badge>
                </div>
            )}

            <Tabs defaultValue="payment" value={activeTab} onValueChange={handleTabChange}>
                <TabsList className="mb-6">
                    <TabsTrigger value="info" className="w-full">
                        {t('myPage.personalInfo')}
                    </TabsTrigger>
                    <TabsTrigger value="payment" className="w-full">
                        {t('myPage.paymentManagement')}
                    </TabsTrigger>
                    <TabsTrigger value="subscriptions" className="w-full">
                        {t('myPage.subscriptionManagement')}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="info" className="max-md:px-4 pb-20">
                    <div className="max-w-[500px] mx-auto">
                        <div className="w-full text-[18px] font-medium mb-4">{t('myPage.editInfo')}</div>
                        <div className="w-full flex flex-col gap-3">
                            <NameEditForm
                                currentName={userName}
                                ownerId={ownerInfo?.id || ''}
                                onSuccess={handleSuccessUpdateName}
                            />
                            {email && (
                                <Card className="dark:bg-[#2e2e2e]">
                                    <Label variant="form">{t('myPage.email')}</Label>
                                    <Input placeholder={t('myPage.enterEmail')} value={email} disabled />
                                    <Button size="lg" className="w-full disabled:bg-badge mt-10" disabled>
                                        {t('myPage.edit')}
                                    </Button>
                                </Card>
                            )}
                            <div className="flex justify-center mt-[26px]">
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <button className="text-sm text-sub-title font-medium flex items-center hover:underline">
                                            {t('myPage.accountDeletion')}
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogTitle>{t('myPage.confirmAccountDeletion')}</AlertDialogTitle>
                                        <AlertDialogDescription className="text-error">
                                            {t('myPage.deletionWarning')}
                                        </AlertDialogDescription>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>{t('myPage.cancel')}</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => setIsPreventDeletionDialogOpen(true)}>
                                                {t('myPage.delete')}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>

                                {/* 탈퇴 방지 모달 */}
                                <AlertDialog
                                    open={isPreventDeletionDialogOpen}
                                    onOpenChange={setIsPreventDeletionDialogOpen}
                                >
                                    <AlertDialogContent>
                                        <div className="flex-1 flex flex-col justify-center items-center">
                                            <AlertDialogTitle className="flex flex-col items-center gap-5">
                                                <Image src={SadIcon} darkSrc={SadDarkIcon} />
                                                <span className="text-[20px]">{t('myPage.leaveEureka')}</span>
                                            </AlertDialogTitle>
                                            <AlertDialogDescription className="text-center mt-2 whitespace-pre-wrap">
                                                {t('myPage.rejoinNotice')}
                                            </AlertDialogDescription>
                                        </div>
                                        <AlertDialogFooter>
                                            <AlertDialogAction
                                                className="flex-1"
                                                onClick={() => {
                                                    toast({ title: t('myPage.deletionComplete') });
                                                    setIsPreventDeletionDialogOpen(false);
                                                }}
                                            >
                                                {t('myPage.confirm')}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="payment" className="max-md:px-4 pb-20">
                    <BillingsPage />
                </TabsContent>
                <TabsContent value="subscriptions" className="max-md:px-4 pb-20">
                    <SubscriptionsPage />
                </TabsContent>
            </Tabs>
        </div>
    );
};
